import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { money, pct, bucketClass, Pill, shortDate } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, ChevronRight } from "lucide-react";

function Stat({ label, value, tone = "", sub }) {
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-4">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"text-xl font-semibold mt-1 tabular-nums " + tone}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </CardContent></Card>
  );
}

export default function Collections() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [aging, setAging] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dash, setDash] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, q, d] = await Promise.all([
        api.get("/collections/aging"),
        api.get("/collections/queue"),
        api.get("/dashboard/collections"),
      ]);
      setAging(a.data); setQueue(q.data); setDash(d.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Always dry-run first: this charges real late fees to real borrowers, so the
  // operator sees what it WILL do before it does it.
  const openRun = async () => {
    setBusy(true);
    try {
      const r = await api.post("/collections/run-arrears", { dry_run: true });
      setPreview(r.data); setRunOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirmRun = async () => {
    setBusy(true);
    try {
      const r = await api.post("/collections/run-arrears", {});
      toast.success(
        `${r.data.delinquent_loans} delinquent loan(s); ${money(r.data.late_fees_charged)} in late fees charged`
      );
      setRunOpen(false); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Collections</h1>
            <p className="text-sm text-slate-500">
              Portfolio at risk, aging, and the accounts that need working today.
            </p>
          </div>
          {isAdmin && (
            <Button onClick={openRun} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="run-arrears-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Run arrears
            </Button>
          )}
        </div>

        {dash && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Portfolio outstanding" value={money(dash.portfolio_outstanding)}
                  sub={`${dash.active_loans} active loan${dash.active_loans === 1 ? "" : "s"}`} />
            <Stat label="Total arrears" value={money(dash.total_arrears)}
                  tone={dash.total_arrears > 0 ? "text-red-700" : ""}
                  sub={`${dash.overdue_loans} overdue`} />
            <Stat label="PAR 30" value={aging ? pct(aging.par30) : "—"}
                  tone={aging && aging.par30 > 10 ? "text-red-700" : ""}
                  sub={aging ? `PAR 90: ${pct(aging.par90)}` : ""} />
            <Stat label="Collected today" value={money(dash.collected_today)}
                  sub={`${dash.payments_today} payment${dash.payments_today === 1 ? "" : "s"}`} />
          </div>
        )}

        <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="font-semibold">Aging</h2>
            <p className="text-xs text-slate-500">
              Outstanding balance by how overdue the account is{aging ? ` · as of ${shortDate(aging.as_of)}` : ""}.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Bucket</th>
                  <th className="text-right px-4 py-2">Loans</th>
                  <th className="text-right px-4 py-2">Outstanding</th>
                  <th className="text-right px-4 py-2">Arrears</th>
                  <th className="text-right px-4 py-2">Share</th>
                </tr>
              </thead>
              <tbody>
                {(aging?.buckets || []).map((b) => {
                  const share = aging.portfolio_outstanding
                    ? (b.outstanding / aging.portfolio_outstanding) * 100 : 0;
                  return (
                    <tr key={b.bucket} className="border-t border-slate-100">
                      <td className="px-4 py-2"><Pill className={bucketClass(b.bucket)}>{b.bucket === "current" ? "Current" : b.bucket + " days"}</Pill></td>
                      <td className="px-4 py-2 text-right tabular-nums">{b.loans}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{money(b.outstanding)}</td>
                      <td className={"px-4 py-2 text-right tabular-nums " + (b.arrears > 0 ? "text-red-700" : "text-slate-400")}>
                        {b.arrears > 0 ? money(b.arrears) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-blue-900" style={{ width: `${Math.min(100, share)}%` }} />
                          </div>
                          <span className="tabular-nums text-xs text-slate-500 w-10 text-right">{share.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {aging && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-semibold">
                    <td className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {aging.buckets.reduce((n, b) => n + b.loans, 0)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(aging.portfolio_outstanding)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {money(aging.buckets.reduce((n, b) => n + b.arrears, 0))}
                    </td>
                    <td className="px-4 py-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {aging && aging.written_off_loans > 0 && (
            <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
              Excludes {aging.written_off_loans} written-off loan(s) totalling {money(aging.written_off_amount)}.
            </div>
          )}
        </CardContent></Card>

        <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="font-semibold">Work queue</h2>
            <p className="text-xs text-slate-500">Overdue accounts, worst first.</p>
          </div>
          {queue.length === 0 ? (
            <div className="p-10 text-center">
              <p className="font-medium">Nothing overdue</p>
              <p className="text-sm text-slate-500 mt-1">Every active loan is current.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {queue.map((l) => (
                <Link key={l.id} to={`/admin/loans/${l.application_id}`}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50"
                      data-testid={`queue-${l.application_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.borrower_email}</div>
                    <div className="text-xs text-slate-500">
                      {l.product_name} · next due {shortDate(l.next_due_date)}
                      {(l.promises || []).length > 0 && (
                        <span className="text-blue-700">
                          {" "}· promised {money(l.promises[0].promised_amount)} by {shortDate(l.promises[0].promised_date)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="font-semibold text-red-700 tabular-nums">{money(l.arrears)}</div>
                      <div className="text-xs text-slate-500">{l.days_past_due} days</div>
                    </div>
                    <Pill className={bucketClass(l.bucket)}>{l.bucket}</Pill>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent></Card>
      </div>

      <AlertDialog open={runOpen} onOpenChange={setRunOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run arrears for the whole portfolio?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview ? (
                <>
                  This scans {preview.loans_scanned} active loan(s). It found{" "}
                  <b>{preview.delinquent_loans} delinquent</b>, and will charge{" "}
                  <b>{money(preview.late_fees_charged)}</b> in late fees.
                  Late fees are charged once per overdue installment, so running this
                  twice in a day changes nothing. Borrowers who fall overdue are notified.
                </>
              ) : "Checking…"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRun} disabled={busy} data-testid="confirm-run-arrears">
              Run it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
